// src/pages/team/TeamTemporaryRolesPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { temporaryRoleStatusMap, getStatusMapping, getEntityId, formatDateTime, getDisplayName, refEntity } from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';

type TempRolesTab = 'all' | 'mine';

export const TeamTemporaryRolesPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t } = useI18n();

  const [activeTab, setActiveTab] = useState<TempRolesTab>('all');
  const [assignments, setAssignments] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [viewingAssignment, setViewingAssignment] = useState<any | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ reason: '', start_at: '', end_at: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const [formData, setFormData] = useState({
    role_id: '',
    assignee_id: '',
    reason: '',
    start_at: '',
    end_at: '',
  });

  const loadAssignments = useCallback(async (tab: TempRolesTab) => {
    if (!id) return;
    try {
      const res = tab === 'mine'
        ? await store.ListMyTemporaryRoles(id)
        : await store.ListTemporaryRoleAssignments(id);
      setAssignments(res.assignments?.assignments ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadAssignments(activeTab);
  }, [id, activeTab, loadAssignments]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setCreating(true);
    try {
      await store.CreateTemporaryRoleAssignment(id, formData as any);
      setFormData({ role_id: '', assignee_id: '', reason: '', start_at: '', end_at: '' });
      setShowCreate(false);
      showSuccess(t('team.assignmentCreated'));
      loadAssignments(activeTab);
    } catch { /* store handles */ } finally { setCreating(false); }
  };

  const handleCancel = async (assignmentId: string) => {
    if (!id) return;
    try {
      await store.CancelTemporaryRoleAssignment(id, assignmentId, { assignment: refEntity(assignmentId), reason: '' });
      showSuccess(t('team.assignmentCancelled'));
      loadAssignments(activeTab);
    } catch { /* store handles */ }
  };

  const handleView = async (a: any) => {
    const aId = getEntityId(a);
    if (!id || !aId) return;
    setViewLoading(true);
    try {
      const res = await store.GetTemporaryRoleAssignment(id, aId);
      setViewingAssignment(res?.assignment ?? a);
    } catch {
      setViewingAssignment(a);
    } finally {
      setViewLoading(false);
    }
  };

  const openEdit = (a: any) => {
    const aId = getEntityId(a);
    setEditingId(aId);
    setEditForm({
      reason: a.reason || '',
      start_at: a.start_at ? String(a.start_at).slice(0, 16) : '',
      end_at: a.end_at ? String(a.end_at).slice(0, 16) : '',
    });
  };

  const handleSaveEdit = async (a: any) => {
    const aId = getEntityId(a);
    if (!id || !aId) return;
    setSavingEdit(true);
    try {
      await store.UpdateTemporaryRoleAssignment(id, aId, {
        assignment: {
          ...a,
          reason: editForm.reason,
          start_at: editForm.start_at ? new Date(editForm.start_at).toISOString() : a.start_at,
          end_at: editForm.end_at ? new Date(editForm.end_at).toISOString() : a.end_at,
        },
      });
      setEditingId(null);
      showSuccess(t('team.assignmentUpdated'));
      loadAssignments(activeTab);
    } catch { /* store handles */ } finally { setSavingEdit(false); }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.temporaryRoles')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="temporary-roles" />


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('team.temporaryRolesTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('team.temporaryRolesDescription')}</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? t('common.cancel') : t('team.createAssignment')}
        </Button>
      </div>

      {store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <div className="flex gap-1 border-b border-border">
        {([
          { id: 'all' as TempRolesTab, label: t('common.all') },
          { id: 'mine' as TempRolesTab, label: t('team.myTemporaryRoles') },
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>{t('team.createAssignment')}</CardTitle>
            <CardDescription>{t('team.createAssignmentDescription')}</CardDescription>
          </CardHeader>
          <form onSubmit={handleCreate}>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label={t('common.role')} value={formData.role_id} onChange={(e) => setFormData((p) => ({ ...p, role_id: e.target.value }))} required />
                <Input label={t('common.assignee')} value={formData.assignee_id} onChange={(e) => setFormData((p) => ({ ...p, assignee_id: e.target.value }))} required />
              </div>
              <Input label={t('common.reason')} value={formData.reason} onChange={(e) => setFormData((p) => ({ ...p, reason: e.target.value }))} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label={t('common.from')} type="datetime-local" value={formData.start_at} onChange={(e) => setFormData((p) => ({ ...p, start_at: e.target.value }))} required />
                <Input label={t('common.to')} type="datetime-local" value={formData.end_at} onChange={(e) => setFormData((p) => ({ ...p, end_at: e.target.value }))} required />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={creating || !formData.role_id.trim() || !formData.assignee_id.trim()}>
                {creating ? t('common.creating') : t('team.createAssignment')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.assignee')}</TableHead>
              <TableHead>{t('common.role')}</TableHead>
              <TableHead>{t('common.reason')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('common.period')}</TableHead>
              <TableHead>{t('common.assignedBy')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">{t('common.loading')}</TableCell>
              </TableRow>
            ) : assignments.length === 0 ? (
              <TableEmpty colSpan={7} message={activeTab === 'mine' ? t('team.noMyTemporaryRoles') : t('team.noTemporaryAssignments')} />
            ) : (
              assignments.map((a: any) => {
                const aId = getEntityId(a);
                const status = a.status || '';
                const canCancel = ['TEMPORARY_ASSIGNMENT_STATUS_SCHEDULED', 'TEMPORARY_ASSIGNMENT_STATUS_ACTIVE'].includes(status);
                const sm = getStatusMapping(temporaryRoleStatusMap, status);
                const isEditingRow = editingId === aId;

                if (isEditingRow) {
                  return (
                    <TableRow key={aId || Math.random()}>
                      <TableCell colSpan={7}>
                        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 py-2">
                          <Input label={t('common.reason')} value={editForm.reason} onChange={(e) => setEditForm((p) => ({ ...p, reason: e.target.value }))} className="flex-1" />
                          <Input label={t('common.from')} type="datetime-local" value={editForm.start_at} onChange={(e) => setEditForm((p) => ({ ...p, start_at: e.target.value }))} />
                          <Input label={t('common.to')} type="datetime-local" value={editForm.end_at} onChange={(e) => setEditForm((p) => ({ ...p, end_at: e.target.value }))} />
                          <div className="flex gap-1.5">
                            <Button size="sm" onClick={() => handleSaveEdit(a)} disabled={savingEdit}>{savingEdit ? t('common.saving') : t('common.saveChanges')}</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>{t('common.cancel')}</Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <TableRow key={aId || Math.random()}>
                    <TableCell><span className="text-sm font-medium">{getDisplayName(a.assignee)}</span></TableCell>
                    <TableCell><span className="text-sm">{a.role?.name || a.role?.id || '--'}</span></TableCell>
                    <TableCell><span className="text-sm text-muted-foreground">{a.reason || '--'}</span></TableCell>
                    <TableCell>
                      <Badge variant={sm.variant} size="sm">
                        {t(sm.labelKey)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(a.start_at)} — {formatDateTime(a.end_at)}
                      </span>
                    </TableCell>
                    <TableCell><span className="text-sm text-muted-foreground">{getDisplayName(a.assigned_by)}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleView(a)}>
                          {t('common.view')}
                        </Button>
                        {canCancel && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(a)}>
                              {t('team.editAssignment')}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleCancel(aId)}>
                              {t('common.cancel')}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {(viewingAssignment || viewLoading) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="button"
          tabIndex={0}
          onClick={() => setViewingAssignment(null)}
          onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') setViewingAssignment(null); }}
        >
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" />
          <Card className="relative w-full max-w-md" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{t('team.assignmentDetailTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {viewLoading || !viewingAssignment ? (
                <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
              ) : (
                <dl className="divide-y divide-border">
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.assignee')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{getDisplayName(viewingAssignment.assignee)}</dd>
                  </div>
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.role')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{viewingAssignment.role?.name || viewingAssignment.role?.id || '--'}</dd>
                  </div>
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.status')}</dt>
                    <dd className="text-sm text-foreground col-span-2">
                      <Badge variant={getStatusMapping(temporaryRoleStatusMap, viewingAssignment.status).variant} size="sm">
                        {t(getStatusMapping(temporaryRoleStatusMap, viewingAssignment.status).labelKey)}
                      </Badge>
                    </dd>
                  </div>
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.reason')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{viewingAssignment.reason || '--'}</dd>
                  </div>
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.period')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{formatDateTime(viewingAssignment.start_at)} — {formatDateTime(viewingAssignment.end_at)}</dd>
                  </div>
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.assignedBy')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{getDisplayName(viewingAssignment.assigned_by)}</dd>
                  </div>
                </dl>
              )}
              <div className="flex justify-end pt-2">
                <Button variant="secondary" onClick={() => setViewingAssignment(null)}>{t('common.close')}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
});

export default TeamTemporaryRolesPage;
